import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Survey from "shared-components/lib/iac-survey/index";
import {
  selectRetrofitSteps,
  selectRetrofitCurrentStep,
  selectCurrentFiltersStepperStep,
  selectShowAxisPopup,
  selectReadOnlyMode,
  selectRetrofitConfiguration,
  selectPartnerPreferences
} from "../../../Retrofit/store/slices/retrofit";
import t from "../../../Common/utils/translation/translator";
import { getSurveyData, logSurveyEvent } from "../../../Common/utils/api/api";
import {
  selectEventsData,
  selectFullScreen,
  selectIsNewSessionId,
  setIsNewSessionId
} from "../../../Common/store/slices/common";
import { selectCartesianConfiguration } from "../../../Cartesian/store/slices/cartesian";
import { selectMc12Configuration } from "../../../Mc12/store/slices/mc12";

const SharedSurvey = ({ disclaimer }) => {
  const dispatch = useDispatch();

  const isFullScreen = useSelector(selectFullScreen);
  const currentStep = useSelector(selectRetrofitCurrentStep);
  const steps = useSelector(selectRetrofitSteps);
  const showAxisPopup = useSelector(selectShowAxisPopup);
  const isNewSessionId = useSelector(selectIsNewSessionId);
  const readOnlyMode = useSelector(selectReadOnlyMode);
  const retrofitConfiguration = useSelector(selectRetrofitConfiguration);
  const cartesianConfiguration = useSelector(selectCartesianConfiguration);
  const mc12Configuration = useSelector(selectMc12Configuration);
  const configuration = retrofitConfiguration || cartesianConfiguration || mc12Configuration;
  const eventsData = useSelector(selectEventsData);
  const partnerPreferences = useSelector(selectPartnerPreferences);
  const currentFiltersStepperStep = useSelector(selectCurrentFiltersStepperStep);

  const [surveyData, setSurveyData] = useState(null);
  const [preselectedAnswers, setPreselectedAnswers] = useState(null);

  const isNewCampaignId =
    window.msizerConfiguration.campaignId &&
    localStorage.getItem(`iac-campaign-${window.msizerConfiguration.campaignId}`) === null;

  const getSurveyType = (actionType) => {
    let surveyTypeStep;
    if (currentStep === steps.SAVEANDSHARE) {
      surveyTypeStep = "generic";
    } else {
      surveyTypeStep = "intermediate";
    }

    return `${surveyTypeStep}_${actionType}`;
  };

  const getSurveyFile = () => {
    switch (currentStep) {
      case steps.SAVEANDSHARE:
        return "generic_survey.json";
      default:
        return "msizer_survey.json";
    }
  };

  const { refetch: execGetSurveyDataJson } = useQuery(
    "GET_SURVEY_DATA_JSON",
    async () => {
      try {
        const surveyDataJson = await getSurveyData(getSurveyFile());
        setSurveyData(surveyDataJson);
        return surveyDataJson;
      } catch (error) {
        console.error("ERR", error.response?.status);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  const getEventStep = () => {
    const eventStepSet = {
      2: eventsData?.tags?.applicationSettingsStep,
      3: eventsData?.tags?.specifySelectRecap,
      3.1: eventsData?.tags?.specifySelectTable,
      3.2: eventsData?.tags?.specifySelectCompare,
      5: eventsData?.tags?.finalizeStep
    };

    const stepId = showAxisPopup ? `${currentStep?.id}.${currentFiltersStepperStep}` : currentStep?.id;

    return eventStepSet[stepId];
  };

  // Get the right preselected answer depending on the current step
  const getPreselectedAnswers = () => {
    const answersKeySet = {
      2: "application",
      3: "specify",
      3.1: "select",
      3.2: "compare"
    };

    const stepId = showAxisPopup ? `${currentStep?.id}.${currentFiltersStepperStep}` : currentStep?.id;

    setPreselectedAnswers({
      "embedded_survey.choose_a_category": `embedded_survey.step.msizer_${answersKeySet[stepId]}`
    });
  };

  useEffect(() => {
    execGetSurveyDataJson();
  }, []);

  useEffect(() => {
    if (currentStep === steps.SAVEANDSHARE) {
      dispatch(setIsNewSessionId(false));
    }
  }, []);

  useEffect(() => {
    if (isNewSessionId && !window.msizerConfiguration.campaignId && currentStep === steps.SAVEANDSHARE) {
      window.dispatchEvent(new Event("iac-force-showing-survey"));
    }
  }, []);

  useEffect(() => {
    if (currentStep !== steps.SAVEANDSHARE) {
      getPreselectedAnswers();
    }
  }, [showAxisPopup, currentFiltersStepperStep]);

  return (
    <div id="overall-shared-survey">
      <Survey
        appTitle={t("msizer.ui.motion-configurator")}
        surveyData={surveyData}
        preselectionnedAnswers={preselectedAnswers}
        getLabel={(key) => t(key)}
        onOpen={() => {
          logSurveyEvent(
            window.msizerConfiguration?.locale || "en-WW",
            getSurveyType("open"),
            getEventStep(),
            configuration?.configId,
            {}
          );
        }}
        onSubmit={(surveyAnswers) => {
          logSurveyEvent(
            window.msizerConfiguration?.locale || "en-WW",
            getSurveyType("sent"),
            getEventStep(),
            configuration?.configId,
            {
              surveyAnswers: JSON.stringify(surveyAnswers)
            }
          );
        }}
        color={partnerPreferences?.styling?.primary || "#008a16"}
        fullScreenEnabled={isFullScreen}
        showDisclaimer={
          disclaimer && !readOnlyMode && ((window.msizerConfiguration.campaignId && isNewSessionId) || isNewCampaignId)
        }
        campaignId={window.msizerConfiguration.campaignId}
      />
    </div>
  );
};

SharedSurvey.propTypes = {
  disclaimer: PropTypes.bool
};
SharedSurvey.defaultProps = {
  disclaimer: false
};

export default SharedSurvey;
